import api from 'api/api';

import { SupplyChainNeighborhood, SupplyChainNode } from './types';

export const MAX_NODES_PER_EXPANSION = 5;

// The API rejects limits outside 1–20 rather than clamping them, and ignores bare `limit`/`offset`.
export type NeighborhoodPageParams = {
  inputs_offset?: number;
  inputs_limit?: number;
  outputs_offset?: number;
  outputs_limit?: number;
};

export const fetchNeighborhood = async (
  nodeId: string,
  version: string,
  system_model: string,
  page: NeighborhoodPageParams = {}
): Promise<SupplyChainNeighborhood> => {
  const { data } = await api.get(`/web/v4/nodes/${nodeId}`, {
    params: { version, system_model, ...page },
  });
  return data;
};

export type DerivedEdge = {
  id: string;
  source_id: string;
  target_id: string;
  amount?: number;
  unit: string;
  flow_object: string;
  child_id: string;
  is_functional?: boolean;
};

export type NodeCounts = Record<string, { inputs?: number; outputs?: number }>;
export type ShownCounts = Record<string, { inputs: number; outputs: number }>;

export const countsFromNodes = (nodes: SupplyChainNode[]): NodeCounts =>
  nodes.reduce<NodeCounts>((acc, n) => {
    if (n.input_count !== undefined && n.output_count !== undefined) {
      acc[n.id] = { inputs: n.input_count, outputs: n.output_count };
    }
    return acc;
  }, {});

export const edgeId = (sourceId: string, targetId: string) => `${sourceId}->${targetId}`;

export const expansionOwnerId = (key: string) => key.replace(/_inputs$|_outputs$/, '');

export const makeInputEdges = (focalId: string, inputs: SupplyChainNode[]): DerivedEdge[] =>
  inputs
    .filter((n) => n.id !== focalId)
    .map((n) => ({
      id: edgeId(n.id, focalId),
      source_id: n.id,
      target_id: focalId,
      amount: n.amount,
      unit: n.unit,
      flow_object: n.flow_object ?? '',
      child_id: n.id,
      is_functional: n.is_functional,
    }));

export const makeOutputEdges = (focalId: string, outputs: SupplyChainNode[]): DerivedEdge[] =>
  outputs
    .filter((n) => n.id !== focalId)
    .map((n) => ({
      id: edgeId(focalId, n.id),
      source_id: focalId,
      target_id: n.id,
      amount: n.amount,
      unit: n.unit,
      flow_object: n.flow_object ?? '',
      child_id: n.id,
      is_functional: n.is_functional,
    }));

export const functionalChildIds = (edges: DerivedEdge[]): Set<string> =>
  new Set(edges.filter((e) => e.is_functional).map((e) => e.child_id));

export type ExpansionResolution = {
  nodesToAdd: SupplyChainNode[];
  instances: SupplyChainNode[];
};

export const resolveExpansionInstances = (
  focalId: string,
  candidates: SupplyChainNode[],
  existingIds: Set<string>,
  targetDepth: number,
  isAncestor: (candidateId: string) => boolean,
  getDepth: (id: string) => number
): ExpansionResolution => {
  const nodesToAdd: SupplyChainNode[] = [];
  const instances: SupplyChainNode[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates) {
    let instance = candidate;
    let isNew = !existingIds.has(candidate.id);

    if (!isNew && isAncestor(candidate.id) && getDepth(candidate.id) !== targetDepth) {
      const compositeId = `${candidate.id}#${focalId}`;
      instance = { ...candidate, id: compositeId, datasetId: candidate.id };
      isNew = !existingIds.has(compositeId);
    }

    if (instance.id === focalId || seen.has(instance.id)) continue;
    seen.add(instance.id);

    instances.push(instance);
    if (isNew) nodesToAdd.push(instance);
  }

  return { nodesToAdd, instances };
};

export const mergeNodes = (prev: SupplyChainNode[], incoming: SupplyChainNode[]): SupplyChainNode[] => [
  ...prev,
  ...incoming.filter((n) => !prev.find((e) => e.id === n.id)),
];

export const mergeEdges = (prev: DerivedEdge[], incoming: DerivedEdge[]): DerivedEdge[] => [
  ...prev,
  ...incoming.filter((e) => !prev.find((ex) => ex.id === e.id)),
];

export const collectNodesToRemove = (
  directIds: string[],
  addedByExpansion: Record<string, Set<string>>,
  protectedNodes: Set<string>
): Set<string> => {
  const toRemove = new Set<string>();
  const visit = (id: string) => {
    if (toRemove.has(id) || protectedNodes.has(id)) return;
    toRemove.add(id);
    addedByExpansion[`${id}_inputs`]?.forEach((childId) => visit(childId));
    addedByExpansion[`${id}_outputs`]?.forEach((childId) => visit(childId));
  };
  directIds.forEach(visit);
  return toRemove;
};
